import { AppPageLayout } from "@/components/common/AppPageLayout";
import Spinner from "@/components/common/Spinner";
import Stepper from "@/components/common/Stepper";
import { TemplateLockProvider } from "@/features/templates/components/TemplateScopeLock";
import { hasTemplateAiDraftMarker, useTemplateAiDraftHydrated } from "@/features/ai-builder/useTemplateAiDraftStore";
import { TemplateAiAssistant } from "@/features/ai-builder/TemplateAiAssistant";
import { selectFormsLiteQueryOptions } from "@/features/forms/api/selectForms";
import { schedulesQueryOptions } from "@/features/schedules/api/selectSchedules";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useSuperUserView } from "@/features/navigation/hooks/useSuperUserView";
import { templateStepperLabel, useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { hooks as h } from "@/hooks";
import { useAuth } from "@/utils/auth";
import { createFileRoute, Outlet, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";

const ADMIN_STEP_PATHS = ["/screener", "/consent", "/hospital-number", "/labels"];

const NewTemplateFormLayout = () => {
    const { templateId } = Route.useParams();
    const router = useRouter();
    const navigate = useNavigate();
    const currentPath = router.state.location.pathname;
    const { steps, setAdminView: setWizardAdminView } = useTemplateNavigationStore();
    const { isSuperUserView } = useSuperUserView();

    // Keep the wizard's steps in line with the super user view switch.
    useEffect(() => {
        setWizardAdminView(isSuperUserView);
    }, [isSuperUserView, setWizardAdminView]);

    // Switching the super user view off while on a super user step moves the user back to the Date step.
    useEffect(() => {
        const onAdminStep = ADMIN_STEP_PATHS.some((path) => currentPath.includes(path));
        if (!isSuperUserView && onAdminStep) {
            void navigate({ to: "/templates/$templateId/date", params: { templateId } });
        }
    }, [isSuperUserView, currentPath, navigate, templateId]);
    const copy = useTemplatesCopy();
    const localizedSteps = useMemo(
        () => steps.map((step) => ({ ...step, words: templateStepperLabel(step.key, copy) })),
        [steps, copy]
    );
    const auth = useAuth();
    const hydrated = useTemplateAiDraftHydrated();
    const waitingForAiDraft = Boolean(auth?.id) && hasTemplateAiDraftMarker(templateId, auth.id) && !hydrated;

    h.useNav([
        { label: copy.templates, route: { to: "/templates" } },
        {
            label: copy.newTemplate,
            route: {
                to: Route.to,
                params: { templateId: templateId },
            },
        },
    ]);

    const shouldShowStepper = useMemo(() => {
        return !currentPath.includes("/publish");
    }, [currentPath]);

    return (
        <AppPageLayout
            width={isSuperUserView ? "full" : "md"}
            padding={isSuperUserView ? "lg" : "none"}
            as="div"
            className="space-y-6"
        >
            {shouldShowStepper && (
                <div className="space-y-3">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            <Stepper steps={localizedSteps} />
                        </div>
                        {isSuperUserView && <TemplateAiAssistant templateId={templateId} />}
                    </div>
                </div>
            )}
            {waitingForAiDraft ? (
                <div className="flex justify-center py-16">
                    <Spinner className="h-10 w-10" />
                </div>
            ) : (
                <TemplateLockProvider templateId={templateId}>
                    <Outlet />
                </TemplateLockProvider>
            )}
        </AppPageLayout>
    );
};

export const Route = createFileRoute("/_app/templates/$templateId")({
    component: NewTemplateFormLayout,
    loader: async ({ context }) => {
        await Promise.all([
            context.queryClient.ensureQueryData(selectFormsLiteQueryOptions()),
            context.queryClient.ensureQueryData(schedulesQueryOptions()),
        ]);
    },
});
